import React from "react";
import { useGetVideoViews } from "../../../api/queries";
import { UserSegmentEnum } from "../../../types";
import Card from "../../../componentLibrary/Card";
import Spinner from "../../../componentLibrary/Spinner";

interface VideoAnalyticsWidgetProps {
  selectedSegments: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
}

const VideoAnalyticsWidget: React.FC<VideoAnalyticsWidgetProps> = ({
  selectedSegments,
  startDate,
  endDate,
}) => {
  const { data: videoViews = [], isLoading } = useGetVideoViews(
    selectedSegments,
    startDate,
    endDate,
  );

  const totalViews = videoViews.reduce((sum, video) => sum + video.count, 0);
  const uniqueVideos = videoViews.length;

  return (
    <Card className="h-full space-y-4">
      <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
        Video Analytics
      </h3>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-100 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-electrum-300">
            {totalViews}
          </div>
          <div className="text-sm text-gray-600 mt-2">Total Video Plays</div>
        </div>
        <div className="bg-gray-100 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-electrum-300">
            {uniqueVideos}
          </div>
          <div className="text-sm text-gray-600 mt-2">Unique Videos</div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner className="mx-auto" />
        </div>
      ) : videoViews.length === 0 ? (
        <div className="text-center p-4 text-gray-500">
          No video plays found for the selected period.
        </div>
      ) : (
        <div className="overflow-y-auto" style={{ maxHeight: "500px" }}>
          <div className="space-y-0">
            {videoViews.map((video) => (
              <div
                key={`${video.pageId || video.pageIndex}-${video.videoUrl}-${video.lessonId}`}
                className="border-2 border-gray-200 rounded-lg p-4 bg-white hover:border-gray-300 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <a
                      href={video.videoUrl}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Video from {video.pageTitle}
                    </a>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-electrum-300">
                        {video.count}
                      </div>
                      <div className="text-xs text-gray-500">plays</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-600">
                        {video.uniqueUserCount}
                      </div>
                      <div className="text-xs text-gray-500">users</div>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {video.moduleTitle} - {video.lessonTitle}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default VideoAnalyticsWidget;
